<script setup>
import { computed } from 'vue';
import Slider from 'dashboard/components-next/slider/Slider.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  speed: {
    type: [Number, String],
    default: 1.0,
  },
  format: {
    type: String,
    default: 'mp3',
  },
});

const emit = defineEmits(['update:speed', 'update:format']);

const { t } = useI18n();

// Helper to convert string to number
const toNumber = val => (typeof val === 'string' ? parseFloat(val) : val);

const speedValue = computed({
  get: () => toNumber(props.speed),
  set: val => emit('update:speed', toNumber(val)),
});

const formatValue = computed({
  get: () => props.format,
  set: val => emit('update:format', val),
});

// Audio format options
const audioFormats = [
  {
    value: 'mp3',
    label: 'MP3',
    description: 'Mais compatível',
    icon: 'i-lucide-music',
  },
  {
    value: 'opus',
    label: 'Opus',
    description: 'Menor tamanho',
    icon: 'i-lucide-file-audio',
  },
  {
    value: 'aac',
    label: 'AAC',
    description: 'Ideal para iOS',
    icon: 'i-lucide-smartphone',
  },
  {
    value: 'flac',
    label: 'FLAC',
    description: 'Sem perdas',
    icon: 'i-lucide-disc-3',
  },
  {
    value: 'wav',
    label: 'WAV',
    description: 'Não comprimido',
    icon: 'i-lucide-audio-waveform',
  },
  {
    value: 'pcm',
    label: 'PCM',
    description: 'Áudio raw',
    icon: 'i-lucide-binary',
  },
];
</script>

<template>
  <div class="space-y-5">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-sliders-horizontal size-5 text-n-brand" />
      <h4 class="text-base font-semibold text-n-slate-12">
        {{ t('AI_AGENTS.VOICE_SETTINGS.TITLE') }}
      </h4>
    </div>

    <!-- Speed Slider -->
    <div class="space-y-3 pb-5 border-b border-n-weak">
      <div class="flex items-center justify-between">
        <div>
          <label class="text-sm font-semibold text-n-slate-12">
            {{ t('AI_AGENTS.VOICE_SETTINGS.SPEED') }}
          </label>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{ t('AI_AGENTS.VOICE_SETTINGS.SPEED_DESC') }}
          </p>
        </div>
        <span
          class="text-sm font-bold font-mono text-n-brand bg-n-brand/10 px-2 py-1 rounded min-w-[3.5rem] text-center"
        >
          {{ Number(speedValue).toFixed(2) }}x
        </span>
      </div>

      <div class="pt-1">
        <Slider v-model="speedValue" :min="0.25" :max="4.0" :step="0.05" />
        <div
          class="flex justify-between text-xs font-medium text-n-slate-10 mt-2"
        >
          <span>0.25x</span>
          <span>1.0x</span>
          <span>4.0x</span>
        </div>
      </div>
    </div>

    <!-- Audio Format -->
    <div class="space-y-3">
      <div>
        <label class="text-sm font-semibold text-n-slate-12">
          {{ t('AI_AGENTS.VOICE_SETTINGS.FORMAT') }}
        </label>
        <p class="text-xs text-n-slate-11 mt-0.5">
          {{ t('AI_AGENTS.VOICE_SETTINGS.FORMAT_DESC') }}
        </p>
      </div>

      <div class="grid grid-cols-3 gap-2">
        <button
          v-for="fmt in audioFormats"
          :key="fmt.value"
          type="button"
          class="flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all"
          :class="
            formatValue === fmt.value
              ? 'border-n-brand bg-n-brand/10'
              : 'border-n-weak bg-n-solid-3 hover:border-n-slate-8'
          "
          @click="formatValue = fmt.value"
        >
          <span
            :class="fmt.icon"
            class="size-4"
            :style="{
              color: formatValue === fmt.value ? 'rgb(var(--blue-9))' : '',
            }"
          />
          <span
            class="text-sm font-medium"
            :class="
              formatValue === fmt.value ? 'text-n-brand' : 'text-n-slate-12'
            "
          >
            {{ fmt.label }}
          </span>
          <span class="text-[10px] text-n-slate-11">
            {{ fmt.description }}
          </span>
        </button>
      </div>
    </div>

    <!-- Info message -->
    <p
      class="text-xs text-n-slate-11 bg-n-alpha-2 rounded-lg p-3 mt-4 border-l-4 border-n-brand"
    >
      {{ t('AI_AGENTS.VOICE_SETTINGS.OPENAI_INFO') }}
    </p>
  </div>
</template>
